/**
 * Utility functions for determining shift status in group shifts
 *
 * For DSPD group shifts, caregivers have individual clock-in/out times.
 * This utility helps determine the status from a specific caregiver's perspective.
 */

/**
 * Check if a shift is a DSPD group shift
 * @param {Object} shift - The shift object
 * @returns {boolean} True if this is a DSPD group shift
 */
// Display label for a caregiver: name plus a role suffix for
// shadow/training assignments (primaries show the bare name)
export function caregiverNameWithRole(caregiver) {
  if (!caregiver) return '';
  const name =
    caregiver.name ||
    `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim();
  const role = caregiver.role || 'primary';
  if (role === 'primary') return name;
  return `${name} (${role.charAt(0).toUpperCase() + role.slice(1)})`;
}

// The primary caregiver of a shift (legacy rows without roles are all
// primary; fall back to the first caregiver)
export function primaryCaregiverOf(shift) {
  const caregivers = (shift && shift.caregivers) || [];
  if (caregivers.length === 0) return null;
  return caregivers.find((c) => (c.role || 'primary') === 'primary') || caregivers[0];
}

export function isDSPDGroupShift(shift) {
  if (!shift) return false;

  // Use pre-computed flag from API if available (set by getCurrent endpoint)
  if (shift.is_dspd_group_shift === true) return true;
  if (shift.is_dspd_group_shift === false) return false;

  // Fall back to computing from raw data
  if (shift.shift_type !== 'Group') return false;
  let payer = shift.payer;
  if (!payer) {
    const clients = shift.clients || [];
    if (clients.length > 0 && clients[0].payer_type) {
      payer = clients[0].payer_type;
    }
  }
  if (payer !== 'DSPD' && payer !== 'Private') return false;

  // Check if shift has DSG/DSP or PP-DSG/PP-DSP service codes
  const serviceCodes = shift.service_codes || [];
  return serviceCodes.some(sc => ['DSG', 'DSP', 'PP-DSG', 'PP-DSP'].includes(sc.code));
}

/**
 * Get the shift status for a specific caregiver in a group shift
 * For DSPD group shifts, returns individual caregiver status.
 * For regular shifts, returns the overall shift status.
 *
 * @param {Object} shift - The shift object with caregiverClockTimes
 * @param {Number|String} caregiverId - The ID of the caregiver
 * @returns {String} Status: 'scheduled', 'clocked-in', 'clocked-out', 'approved', etc.
 */
/**
 * Check if a shift's date has passed (i.e. it's now the next day or later)
 */
function isShiftDatePast(shift) {
  const shiftDate = shift.date || shift.scheduled_date;
  if (!shiftDate) return false;
  const dateStr = shiftDate.split('T')[0];
  const today = new Date();
  const todayStr = today.getFullYear() + '-' +
    String(today.getMonth() + 1).padStart(2, '0') + '-' +
    String(today.getDate()).padStart(2, '0');
  return dateStr < todayStr;
}

export function getCaregiverShiftStatus(shift, caregiverId) {
  if (!shift) return 'scheduled';

  // For potential shifts, always return scheduled
  if (shift.isPotential) {
    return 'scheduled';
  }

  // Shadow/training caregivers on non-group shifts have their own clock
  // record (side row) — their status is theirs, not the shift-level one.
  // Applies when the API says the requester is a side actor (my_role) or
  // when this caregiver has a side row on a non-group shift.
  const isNonGroup = (shift.shift_type || '1-on-1') !== 'Group';
  const hasSideRow =
    isNonGroup &&
    (shift.caregiverClockTimes || []).some(
      (ct) => Number(ct.id_users) === Number(caregiverId),
    );
  const isSideActor =
    isNonGroup &&
    ((shift.my_role && shift.my_role !== 'primary') || hasSideRow);

  // For non-group shifts (primary caregiver) use the main shift_status
  if (!isDSPDGroupShift(shift) && !isSideActor) {
    const status = shift.shift_status || shift.status;
    if (status) {
      return normalizeStatus(status);
    }
    // No status set — check if the shift date has passed
    if (isShiftDatePast(shift)) {
      // No clock-in and date is past → missed
      if (!shift.clockIn && !shift.clock_in) {
        return 'missed';
      }
      // Clocked in but never clocked out → error
      if (!shift.clockOut && !shift.clock_out) {
        return 'error';
      }
    }
    return 'scheduled';
  }

  // For DSPD group shifts, use pre-computed my_clock_status from API if available
  if (shift.my_clock_status) {
    return normalizeStatus(shift.my_clock_status);
  }

  // Fall back to computing from caregiverClockTimes
  const caregiverClockTimes = shift.caregiverClockTimes || [];
  const caregiverClockTime = caregiverClockTimes.find(
    ct => Number(ct.id_users) === Number(caregiverId)
  );
  const overallStatus = shift.shift_status || shift.status;
  const dayPast = isShiftDatePast(shift);

  // Determine status based on their individual clock times
  if (!caregiverClockTime || !caregiverClockTime.clock_in) {
    // This caregiver hasn't clocked in
    if (overallStatus && overallStatus.toLowerCase() === 'approved') {
      return 'approved';
    }
    if (overallStatus && overallStatus.toLowerCase() === 'missed') {
      return 'missed';
    }
    // Day has passed and they never clocked in → missed
    if (dayPast) {
      return 'missed';
    }
    return 'scheduled';
  } else if (caregiverClockTime.clock_in && !caregiverClockTime.clock_out) {
    // This caregiver has clocked in but not out
    // Day has passed and they didn't clock out → error
    if (dayPast) {
      return 'error';
    }
    return 'clocked-in';
  } else if (caregiverClockTime.clock_out) {
    // This caregiver has completed their shift
    if (overallStatus && overallStatus.toLowerCase() === 'approved') {
      return 'approved';
    }
    return 'clocked-out';
  }

  return 'scheduled'; // Default
}

/**
 * Normalize status strings to consistent format
 * @param {String} status - The status string
 * @returns {String} Normalized status
 */
function normalizeStatus(status) {
  if (!status) return 'scheduled';

  const lower = status.toLowerCase();
  if (lower === 'unassigned') return 'unassigned';
  if (lower === 'approved') return 'approved';
  if (lower === 'clocked out') return 'clocked-out';
  if (lower === 'clocked in') return 'clocked-in';
  if (lower === 'not clocked in') return 'scheduled';
  if (lower === 'missed') return 'missed';
  if (lower === 'error') return 'error';
  if (lower === 'canceled') return 'canceled';
  if (lower === 'scheduled') return 'scheduled';

  return 'scheduled';
}

/**
 * Get all caregiver statuses for admin view
 * Returns an array with status for each caregiver in the shift
 *
 * @param {Object} shift - The shift object
 * @returns {Array} Array of caregiver status objects
 */
export function getAllCaregiverStatuses(shift) {
  if (!shift || !shift.caregivers) return [];

  const caregivers = shift.caregivers || [];
  const clockTimes = shift.caregiverClockTimes || [];
  const isGroupShift = isDSPDGroupShift(shift);

  return caregivers.map(caregiver => {
    if (!isGroupShift) {
      // For regular shifts, all caregivers share the same status
      const computedStatus = getCaregiverShiftStatus(shift, caregiver.id);
      return {
        caregiverId: caregiver.id,
        caregiverName: `${caregiver.first_name} ${caregiver.last_name}`,
        status: computedStatus,
        icon: getStatusIcon(computedStatus),
        color: getStatusColor(computedStatus),
        clockIn: shift.clock_in,
        clockOut: shift.clock_out
      };
    }

    // For DSPD group shifts, get individual status
    const clockTime = clockTimes.find(ct => Number(ct.id_users) === Number(caregiver.id));

    let status, icon, color, clockIn, clockOut;
    if (!clockTime || !clockTime.clock_in) {
      status = 'scheduled';
      icon = '○'; // Empty circle
      color = 'gray';
      clockIn = null;
      clockOut = null;
    } else if (clockTime.clock_in && !clockTime.clock_out) {
      status = 'clocked-in';
      icon = '◉'; // Clocked in
      color = 'blue';
      clockIn = clockTime.clock_in;
      clockOut = null;
    } else {
      const overallStatus = shift.shift_status || shift.status;
      if (overallStatus && overallStatus.toLowerCase() === 'approved') {
        status = 'approved';
        icon = '✓'; // Checkmark
        color = 'green';
      } else {
        status = 'clocked-out';
        icon = '⬤'; // Filled circle
        color = 'green';
      }
      clockIn = clockTime.clock_in;
      clockOut = clockTime.clock_out;
    }

    return {
      caregiverId: caregiver.id,
      caregiverName: `${caregiver.first_name} ${caregiver.last_name}`,
      status,
      icon,
      color,
      clockIn,
      clockOut
    };
  });
}

/**
 * Get icon for status badge
 * @param {String} status - Normalized status
 * @returns {String} Icon character
 */
export function getStatusIcon(status) {
  switch (status) {
    case 'scheduled':
    case 'unassigned':
      return '○';
    case 'clocked-in':
      return '◉';
    case 'clocked-out':
      return '⬤';
    case 'approved':
      return '✓';
    case 'missed':
      return '✗';
    case 'error':
      return '✗';
    default:
      return '○';
  }
}

/**
 * Get color for status badge
 * @param {String} status - Normalized status
 * @returns {String} Color name
 */
function getStatusColor(status) {
  switch (status) {
    case 'scheduled':
      return 'gray';
    case 'unassigned':
      return 'orange';
    case 'clocked-in':
      return 'blue';
    case 'clocked-out':
      return 'green';
    case 'approved':
      return 'green';
    case 'missed':
      return 'red';
    case 'error':
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get CSS color for status badge dot (matches app's status badge colors)
 * @param {String} status - Normalized status
 * @returns {String} CSS color value
 */
export function getStatusBadgeColor(status) {
  switch (status) {
    case 'scheduled':
      return 'rgba(13, 110, 253, 0.8)';   // Blue
    case 'unassigned':
      return 'rgba(32, 201, 151, 0.8)';   // Teal
    case 'clocked-in':
      return 'rgba(255, 193, 7, 0.9)';    // Yellow
    case 'clocked-out':
      return 'rgba(237, 137, 54, 0.7)';   // Orange
    case 'approved':
      return 'rgba(156, 39, 176, 0.7)';   // Purple
    case 'missed':
      return 'rgba(108, 117, 125, 0.8)';  // Grey
    case 'error':
      return 'rgba(220, 53, 69, 0.8)';   // Red
    default:
      return 'rgba(13, 110, 253, 0.8)';   // Blue
  }
}

/**
 * Format a UTC datetime string to local 12-hour time (e.g. "1:40pm")
 */
function formatClockTimeShort(utcDateTimeStr) {
  if (!utcDateTimeStr) return null;
  const d = new Date(utcDateTimeStr);
  if (isNaN(d.getTime())) return null;
  let hours = d.getHours();
  const minutes = d.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${hours}:${String(minutes).padStart(2, '0')}${ampm}`;
}

/**
 * Calculate hours between two UTC datetime strings, returned as decimal (e.g. "3.75")
 */
function calculateHoursWorked(clockIn, clockOut) {
  if (!clockIn || !clockOut) return null;
  const start = new Date(clockIn);
  const end = new Date(clockOut);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  const diffMs = end - start;
  const hours = diffMs / (1000 * 60 * 60);
  return Math.round(hours * 100) / 100;
}

/**
 * Generate HTML for caregiver status lines in tooltips
 * @param {Object} shift - The raw shift object (originalData)
 * @param {Array} caregivers - Array of caregiver objects with id and name
 * @returns {String} HTML string with colored dots, names, times, and hours
 */
export function formatCaregiverStatusHtml(shift, caregivers) {
  if (!caregivers || caregivers.length === 0) return '';
  const clockTimes = shift.caregiverClockTimes || [];
  return caregivers.map(caregiver => {
    const status = getCaregiverShiftStatus(shift, caregiver.id);
    const color = getStatusBadgeColor(status);
    const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px;vertical-align:middle;"></span>`;
    const clockTime = clockTimes.find(ct => Number(ct.id_users) === Number(caregiver.id));

    let timeInfo = '';
    if (clockTime && clockTime.clock_in) {
      const inTime = formatClockTimeShort(clockTime.clock_in);
      const outTime = formatClockTimeShort(clockTime.clock_out);
      if (inTime && outTime) {
        const hours = calculateHoursWorked(clockTime.clock_in, clockTime.clock_out);
        timeInfo = `, ${inTime} - ${outTime}, ${hours} hours`;
      } else if (inTime) {
        timeInfo = `, ${inTime} - In Progress`;
      }
    }

    return `${dot}${caregiver.name}${timeInfo}`;
  }).join('<br>');
}

/**
 * Check if a caregiver can clock in to a shift
 * For DSPD group shifts, checks if THIS caregiver has already clocked in
 *
 * @param {Object} shift - The shift object
 * @param {Number|String} caregiverId - The ID of the caregiver
 * @returns {boolean} True if caregiver can clock in
 */
export function canCaregiverClockIn(shift, caregiverId) {
  const status = getCaregiverShiftStatus(shift, caregiverId);

  // Can only clock in if status is 'scheduled' or 'unassigned'
  return status === 'scheduled' || status === 'unassigned';
}

/**
 * Check if a caregiver can clock out of a shift
 * For DSPD group shifts, checks if THIS caregiver has clocked in
 *
 * @param {Object} shift - The shift object
 * @param {Number|String} caregiverId - The ID of the caregiver
 * @returns {boolean} True if caregiver can clock out
 */
export function canCaregiverClockOut(shift, caregiverId) {
  const status = getCaregiverShiftStatus(shift, caregiverId);

  // Can only clock out if status is 'clocked-in'
  return status === 'clocked-in';
}

/**
 * Check if all group shift tasks are complete for a specific caregiver
 * Returns true when the caregiver has no remaining tasks (or the shift is not a group shift).
 *
 * @param {Object} shift - The shift object with clientDetails
 * @param {Number|String} caregiverId - The ID of the caregiver
 * @returns {boolean} True if all tasks are complete
 */
export function areAllGroupTasksComplete(shift, caregiverId) {
  if (!isDSPDGroupShift(shift) || !shift.clientDetails) return true;

  const details = shift.clientDetails;
  const id = Number(caregiverId);

  // Mirrors the server-side clock-out gate (controllers/shifts.js clockOut):
  // each task blocks only the caregiver it is assigned to, and medications
  // gate on medications_caregiver_id with an unset value counting as incomplete.
  return details.every(cd => {
    // If client did not attend, all tasks for this client are considered complete
    if (cd.did_not_attend) return true;
    const pickupOk = Number(cd.pickup_caregiver_id) !== id || cd.pickup_time;
    const dropoffOk = Number(cd.dropoff_caregiver_id) !== id || cd.dropoff_time;
    const notesOk = Number(cd.shift_notes_caregiver_id) !== id || cd.shift_notes_completed;
    const medsOk = Number(cd.medications_caregiver_id) !== id || cd.medications_completed;
    return pickupOk && dropoffOk && notesOk && medsOk;
  });
}
